import React, { useState, useEffect, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';

interface PlaceholderInfo {
  pos: number;
  description: string;
  id: string;
}

interface PlaceholderNavigatorProps {
  editor: Editor;
}

export const PlaceholderNavigator: React.FC<PlaceholderNavigatorProps> = ({ editor }) => {
  const [placeholders, setPlaceholders] = useState<PlaceholderInfo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  // Scan document for placeholder nodes
  const scanPlaceholders = useCallback(() => {
    const found: PlaceholderInfo[] = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'placeholder') {
        found.push({
          pos,
          description: node.attrs.description || 'Enter value',
          id: node.attrs.id || '',
        });
      }
    });
    setPlaceholders(found);

    // If current index is out of bounds, reset
    if (found.length === 0) {
      setCurrentIndex(-1);
    } else if (currentIndex >= found.length) {
      setCurrentIndex(found.length - 1);
    }
  }, [editor, currentIndex]);

  // Rescan on every editor update
  useEffect(() => {
    scanPlaceholders();
    editor.on('update', scanPlaceholders);
    return () => {
      editor.off('update', scanPlaceholders);
    };
  }, [editor, scanPlaceholders]);

  // Navigate to a placeholder by index
  const navigateTo = useCallback((index: number) => {
    if (index < 0 || index >= placeholders.length) return;
    setCurrentIndex(index);

    const { pos } = placeholders[index];
    const resolvedPos = editor.state.doc.resolve(pos);
    const node = editor.state.doc.nodeAt(pos);
    if (!node) return;

    // Create a NodeSelection on the placeholder
    const selection = NodeSelection.create(editor.state.doc, resolvedPos.pos);
    const tr = editor.state.tr.setSelection(selection).scrollIntoView();
    editor.view.dispatch(tr);
    editor.view.focus();
  }, [editor, placeholders]);

  const navigateNext = useCallback(() => {
    if (placeholders.length === 0) return;
    const next = currentIndex < 0 ? 0 : (currentIndex + 1) % placeholders.length;
    navigateTo(next);
  }, [currentIndex, placeholders.length, navigateTo]);

  const navigatePrev = useCallback(() => {
    if (placeholders.length === 0) return;
    const prev = currentIndex <= 0 ? placeholders.length - 1 : currentIndex - 1;
    navigateTo(prev);
  }, [currentIndex, placeholders.length, navigateTo]);

  // Listen for placeholder:navigate custom events from the Placeholder extension keyboard shortcuts
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.direction === 'next') navigateNext();
      else if (detail?.direction === 'prev') navigatePrev();
    };
    window.addEventListener('placeholder:navigate', handler);
    return () => window.removeEventListener('placeholder:navigate', handler);
  }, [navigateNext, navigatePrev]);

  // Don't render if there are no placeholders
  if (placeholders.length === 0) return null;

  const displayIndex = currentIndex < 0 ? '–' : currentIndex + 1;

  return (
    <div className="placeholder-navigator">
      <div className="placeholder-navigator-icon">?</div>
      <span className="placeholder-navigator-label">
        {placeholders.length} placeholder{placeholders.length !== 1 ? 's' : ''} to fill
      </span>
      <div className="placeholder-navigator-controls">
        <span className="placeholder-navigator-counter">
          {displayIndex} / {placeholders.length}
        </span>
        <button
          onClick={navigatePrev}
          className="placeholder-navigator-btn"
          title="Previous placeholder (Shift+Tab)"
          aria-label="Previous placeholder"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button
          onClick={navigateNext}
          className="placeholder-navigator-btn"
          title="Next placeholder (Tab)"
          aria-label="Next placeholder"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};
